import React, { useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useResource } from "@/hooks/useResource";
import { Gear, Moon, Sun, Palette, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ACCENTS = [["Electric Violet", "#7C3AED"], ["Hot Pink", "#EC4899"], ["Digital Blue", "#3B82F6"], ["Aqua Mint", "#2DD4BF"], ["Lime", "#A3E635"], ["Soft Coral", "#FB7185"], ["Warm Yellow", "#FACC15"]];
const CATEGORIES = ["Apparel", "Jewellery", "Handbags", "Footwear", "Beauty", "Accessories", "Other"];

function initials(name) {
  return (name || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "BR";
}

export default function Settings() {
  const { theme, toggleTheme, refreshBrands } = useApp();
  const { user, isApprover } = useAuth();
  const { items: brands, create, update, remove } = useResource("brands");
  const [dialog, setDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});
  const [confirm, setConfirm] = useState(null);

  const setAccent = (hex) => { document.documentElement.style.setProperty("--ring-accent", hex); localStorage.setItem("ae_accent", hex); };

  const openCreate = () => { setEditItem(null); setForm({ name: "", category: "Apparel", color: "#7C3AED", logo: "" }); setDialog(true); };
  const openEdit = (b) => { setEditItem(b); setForm({ ...b }); setDialog(true); };
  const saveBrand = async () => {
    if (!form.name?.trim()) return toast.error("Brand name is required");
    const payload = { name: form.name.trim(), category: form.category || "Other", color: form.color || "#7C3AED", logo: (form.logo || initials(form.name)).slice(0, 2).toUpperCase() };
    try {
      if (editItem) await update.mutateAsync({ id: editItem.id, body: payload });
      else await create.mutateAsync(payload);
      await refreshBrands();
      toast.success(editItem ? "Brand updated" : "Brand created");
      setDialog(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save brand"); }
  };
  const delBrand = async (b) => {
    try { await remove.mutateAsync(b.id); await refreshBrands(); toast.success("Brand deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    setConfirm(null);
  };

  return (
    <div>
      <PageHeader title="Settings & Administration" subtitle="Personalise your workspace" icon={Gear} />
      <Tabs defaultValue="general">
        <TabsList className="rounded-full">
          <TabsTrigger value="general" className="rounded-full" data-testid="settings-general">General</TabsTrigger>
          <TabsTrigger value="brands" className="rounded-full" data-testid="settings-brands">Brands</TabsTrigger>
          <TabsTrigger value="integrations" className="rounded-full" data-testid="settings-integrations">Integrations</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-6 space-y-4">
          <div className="rounded-2xl border border-border bg-surface-raised p-6">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16"><AvatarImage src={user?.avatar} /><AvatarFallback>{user?.name?.[0]}</AvatarFallback></Avatar>
              <div><p className="font-display text-lg font-bold">{user?.name}</p><p className="text-sm capitalize text-muted-foreground">{user?.role?.replace(/_/g, " ")} · {user?.email}</p></div>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-2xl border border-border bg-surface-raised p-6">
            <div className="flex items-center gap-3">{theme === "dark" ? <Moon size={22} weight="duotone" /> : <Sun size={22} weight="duotone" />}<div><p className="font-semibold">Dark mode</p><p className="text-sm text-muted-foreground">Toggle light / dark theme</p></div></div>
            <Switch checked={theme === "dark"} onCheckedChange={toggleTheme} data-testid="settings-theme-switch" />
          </div>
          <div className="rounded-2xl border border-border bg-surface-raised p-6">
            <div className="mb-4 flex items-center gap-3"><Palette size={22} weight="duotone" /><div><p className="font-semibold">Accent colour</p><p className="text-sm text-muted-foreground">Pick your workspace accent</p></div></div>
            <div className="flex flex-wrap gap-3">
              {ACCENTS.map(([name, hex]) => (
                <button key={hex} onClick={() => setAccent(hex)} data-testid={`accent-${hex.replace("#", "")}`} className="flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-sm hover:bg-secondary">
                  <span className="h-4 w-4 rounded-full" style={{ background: hex }} /> {name}
                </button>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="brands" className="mt-6">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Manage the brands under Asymmetry Edits.</p>
            <Button onClick={openCreate} data-testid="add-brand-btn" className="rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white"><Plus size={16} weight="bold" className="mr-1" /> Add Brand</Button>
          </div>
          {brands.length === 0 ? (
            <EmptyState title="No brands yet" subtitle="Add your first brand to start using multi-brand campaigns, calendars and reports." action={<Button onClick={openCreate} className="rounded-full">Add Brand</Button>} />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {brands.map((b) => (
                <div key={b.id} data-testid="brand-card" className="group flex items-center gap-3 rounded-2xl border border-border bg-surface-raised p-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl font-display font-black text-white" style={{ background: b.color }}>{b.logo}</div>
                  <div className="min-w-0 flex-1"><p className="truncate font-semibold">{b.name}</p><p className="text-xs text-muted-foreground">{b.category}</p></div>
                  <div className="flex opacity-0 transition-opacity group-hover:opacity-100">
                    <button onClick={() => openEdit(b)} data-testid="edit-brand" className="rounded-lg p-1.5 text-muted-foreground hover:text-primary"><PencilSimple size={16} /></button>
                    {isApprover && <button onClick={() => setConfirm(b)} data-testid="delete-brand" className="rounded-lg p-1.5 text-muted-foreground hover:text-brand-pink"><Trash size={16} /></button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="integrations" className="mt-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[["Meta Ads", true], ["Google Ads", true], ["Instagram", true], ["WhatsApp Business", false], ["Google Analytics", true], ["Shopify / E-commerce", true], ["ERP Finance", false], ["Google Drive", false], ["Resend Email", false]].map(([name, live]) => (
              <div key={name} className="flex items-center justify-between rounded-2xl border border-border bg-surface-raised p-4" data-testid="integration-item">
                <p className="font-medium">{name}</p>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${live ? "bg-lime-500/15 text-lime-400" : "bg-slate-500/15 text-slate-400"}`}>{live ? "Connected" : "Placeholder"}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">Placeholder connectors use mock data and are clearly labelled. Live API credentials can be added later.</p>
        </TabsContent>
      </Tabs>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="sm:max-w-md" data-testid="brand-dialog">
          <DialogHeader><DialogTitle className="font-display">{editItem ? "Edit Brand" : "New Brand"}</DialogTitle>
            <DialogDescription className="sr-only">Set the brand name, category, logo and accent colour.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl font-display text-lg font-black text-white" style={{ background: form.color || "#7C3AED" }}>{(form.logo || initials(form.name))}</div>
              <div className="flex-1"><Label className="text-xs uppercase text-muted-foreground">Brand name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" data-testid="brand-name" placeholder="e.g. DenimGlow" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase text-muted-foreground">Category</Label><select value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-10 w-full rounded-xl border border-input bg-surface-raised px-3 text-sm" data-testid="brand-category">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
              <div><Label className="text-xs uppercase text-muted-foreground">Logo (2 letters)</Label><Input value={form.logo || ""} onChange={(e) => setForm({ ...form, logo: e.target.value.slice(0, 2).toUpperCase() })} className="rounded-xl" data-testid="brand-logo" placeholder={initials(form.name)} maxLength={2} /></div>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Accent colour</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {ACCENTS.map(([name, hex]) => (
                  <button key={hex} onClick={() => setForm({ ...form, color: hex })} data-testid={`brand-color-${hex.replace("#", "")}`} className={`h-8 w-8 rounded-full transition-transform hover:scale-110 ${form.color === hex ? "ring-2 ring-offset-2 ring-offset-background ring-white" : ""}`} style={{ background: hex }} title={name} />
                ))}
              </div>
            </div>
            <Button onClick={saveBrand} disabled={create.isPending || update.isPending} data-testid="save-brand" className="w-full rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white">{editItem ? "Save changes" : "Create Brand"}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} title={`Delete ${confirm?.name}?`} description="Records tagged to this brand will remain but the brand will disappear from switchers and forms." onConfirm={() => delBrand(confirm)} />
    </div>
  );
}
